import { AxiosError, AxiosInstance, isCancel } from 'axios';
import {
    BASE_CHATBLK_API_PATH,
    BLK_API_PATHS,
    CHAT_BLK,
    CHAT_BLK_BETA,
    DEFAULT,
    ERROR_MESSAGE_SPACER,
    OAUTH_ACCESS_TOKEN,
    URIS,
    XHR,
} from '../constants';
import {
    ChatBlkAppContext,
    Configuration,
    PromptRequestAiEngineEnum,
} from '../types';
import {
    getApiBaseRef,
    getAxiosInstance,
    getHost,
    isLocalhost,
    uuidv4,
} from '../utils';

const meta = window || import.meta.env;

export type PostTelemetryEventsOverride = (events: unknown[]) => void | null;

export class ChatBlkBaseService {
    protected static instance: ChatBlkBaseService;

    protected static chatBlkAppContext?: ChatBlkAppContext;

    protected static axiosInstance: AxiosInstance;
    protected static basePath: string;

    protected static apiConfig: Configuration = new Configuration({
        apiKey: meta.VITE_CHATBLK_API_KEY,
        basePath: BASE_CHATBLK_API_PATH,
    });

    protected constructor(
        appContext?: ChatBlkAppContext,
        protected isChatBlkEmbedded = false,
        protected postTelemetryEventsOverride: PostTelemetryEventsOverride = null
    ) {
        if (ChatBlkBaseService.instance?.appContext) return;
        if (!appContext || !Object.keys(appContext).length) {
            throw new Error(`[${CHAT_BLK}] App context is null or undefined.`);
        }
        const { hostname } = window.location;
        const { aiProps } = appContext;

        ChatBlkBaseService.basePath =
            isLocalhost() || URIS.PRE_PROD_HOSTS.includes(hostname)
                ? getApiBaseRef()
                : getHost(false);

        ChatBlkBaseService.axiosInstance = getAxiosInstance(
            ChatBlkBaseService.basePath,
            DEFAULT.HEADERS
        );

        const {
            appName = CHAT_BLK,
            sessionId = uuidv4(),
            aiEngine = PromptRequestAiEngineEnum.ChatBlkV1,
        } = aiProps || {};

        ChatBlkBaseService.chatBlkAppContext = {
            ...appContext,
            aiProps: {
                ...(ChatBlkBaseService.chatBlkAppContext?.aiProps || {}),
                appName,
                sessionId,
                aiEngine,
            },
        };
    }

    public static getInstance(
        chatBlkAppContext?: ChatBlkAppContext,
        isChatBlkEmbedded = false,
        postTelemetryEventsOverride: PostTelemetryEventsOverride = null
    ): ChatBlkBaseService {
        if (chatBlkAppContext && !ChatBlkBaseService.instance) {
            ChatBlkBaseService.instance = new ChatBlkBaseService(
                chatBlkAppContext,
                isChatBlkEmbedded,
                postTelemetryEventsOverride
            );
        }
        return ChatBlkBaseService.instance;
    }

    public get appContext(): ChatBlkAppContext {
        return ChatBlkBaseService.chatBlkAppContext;
    }

    public get username(): string {
        return ChatBlkBaseService.chatBlkAppContext.username;
    }

    public set username(username: string) {
        ChatBlkBaseService.chatBlkAppContext.username = username;
    }

    public get userInitials(): string {
        return ChatBlkBaseService.chatBlkAppContext.userInitials;
    }

    public set userInitials(userInitials: string) {
        ChatBlkBaseService.chatBlkAppContext.userInitials = userInitials;
    }

    public get email(): string {
        return ChatBlkBaseService.chatBlkAppContext.email;
    }

    public set email(email: string) {
        ChatBlkBaseService.chatBlkAppContext.email = email;
    }

    public get sessionId(): string {
        return ChatBlkBaseService.chatBlkAppContext.aiProps.sessionId;
    }

    public set sessionId(newId: string) {
        ChatBlkBaseService.chatBlkAppContext.aiProps.sessionId =
            newId ?? uuidv4();
    }

    public get hasToken(): boolean {
        return !!sessionStorage.getItem(OAUTH_ACCESS_TOKEN);
    }

    public get isProduction(): boolean {
        return (
            ChatBlkBaseService.chatBlkAppContext.isProd ||
            window['__CYPRESS_CHATBLK_ENV__'] === 'production' ||
            URIS.PROD_HOSTS.includes(window.location.hostname)
        );
    }

    public set isProduction(isProd: boolean) {
        ChatBlkBaseService.chatBlkAppContext.isProd =
            window['__CYPRESS_CHATBLK_ENV__'] === 'production' ||
            isProd ||
            URIS.PROD_HOSTS.includes(window.location.hostname);
    }

    /**
     * Gets a value indicating whether the user is logged into the host application. This *does not* indicate whether the user is
     * logged into the ChatBlk service or has an active token available.
     *
     * This getter determines the login status based on the current hostname and the application's context:
     * - If the application is running on localhost or on one of the pre-production hosts (as specified in URIS.PRE_PROD_HOSTS),
     *   the login status is determined by whether an email is defined.
     * - Otherwise, the login status is determined by the BLK.isCwpUserSignedIn property of the global window object.
     *
     * @returns {boolean} True if the user is considered logged in; otherwise, false.
     */
    public get isLoggedIn(): boolean {
        return isLocalhost() ||
            URIS.PRE_PROD_HOSTS.includes(window.location.hostname)
            ? this.email !== undefined
            : (window as unknown as { BLK: { isCwpUserSignedIn: boolean } })
                  ?.BLK?.isCwpUserSignedIn;
    }

    public get appName(): string {
        return ChatBlkBaseService.chatBlkAppContext.aiProps.appName;
    }

    public get displayAppName(): string {
        const appName = this.appName;
        if (appName === CHAT_BLK || appName === CHAT_BLK_BETA) {
            return appName;
        }
        return (
            ChatBlkBaseService.chatBlkAppContext.displayMessage
                ?.displayAppName || this.appName
        );
    }

    protected static get requestHeaders() {
        const dateNow = new Date();
        const uuid = uuidv4();
        const traceId = uuid.replace(/-/g, '');
        const spanId = traceId.substring(0, 16);

        if (URIS.PRE_PROD_HOSTS.includes(window.location.hostname)) {
            return {
                ...DEFAULT.HEADERS,
                [XHR.REQUEST_ID]: uuid,
                [XHR.ORIGIN_TIMESTAMP]: dateNow.toISOString(),
                [XHR.API_KEY]: `${ChatBlkBaseService.apiConfig.apiKey}`,
                [XHR.AUTH]: meta.VITE_PRE_PROD_AUTH,
            };
        }

        // Get the OpenID Connect (OIDC) token and add to axios header
        const tokenResponse = sessionStorage.getItem(OAUTH_ACCESS_TOKEN);
        if (tokenResponse) {
            const { token_type: tokenType, access_token: accessToken } =
                JSON.parse(tokenResponse);
            return {
                ...DEFAULT.HEADERS,
                [XHR.TRACEPARENT]: `00-${traceId}-${spanId}-01`,
                [XHR.AUTH]: `${tokenType} ${accessToken}`,
            };
        } else {
            throw new Error(
                `[${CHAT_BLK}] Authorization failed. Unable to retrieve an access token.`
            );
        }
    }

    /**
     * Handle XHR errors from axios requests
     */
    protected handleXhrErrors = (
        method: string,
        error: AxiosError,
        payload: unknown,
        requestHeaders: unknown,
        isThrowableError = false
    ) => {
        if (isCancel(error)) return;
        const { name, message, response, request } = error;
        let prefix = `[${CHAT_BLK} ${method}]`;
        let throwableMessage = `${prefix} ${name}: ${message}`;

        if (response) {
            let vResponseId: string;
            const { data = null, status = 500, headers = {} } = response;
            for (const key in headers) {
                if (XHR.RESPONSE_ID_REGEX.test(key)) {
                    vResponseId = headers[key] as string;
                    break;
                }
            }

            const responseId = vResponseId
                ? `${ERROR_MESSAGE_SPACER}Response ID: ${vResponseId}`
                : '';
            prefix = URIS.PRE_PROD_HOSTS.includes(window.location.hostname)
                ? `[${CHAT_BLK} Request Id: ${requestHeaders[XHR.REQUEST_ID]}${ERROR_MESSAGE_SPACER}${XHR.TIMESTAMP}: ${requestHeaders[XHR.ORIGIN_TIMESTAMP]}${responseId}]`
                : `[${CHAT_BLK} ${XHR.TRACEPARENT.toLowerCase()}: ${requestHeaders[XHR.TRACEPARENT]}]`;
            if (data) {
                if (typeof data === 'object' && 'message' in data) {
                    throwableMessage = `${status} ${prefix} ${data.message}`;
                }
            } else {
                throwableMessage = `${status} ${prefix} ${message}`;
            }
            console.error(throwableMessage, {
                data,
                headers,
                payload,
            });
        } else if (request) {
            prefix = URIS.PRE_PROD_HOSTS.includes(window.location.hostname)
                ? `[${CHAT_BLK} Request Id: ${requestHeaders[XHR.REQUEST_ID]}${ERROR_MESSAGE_SPACER}${XHR.TIMESTAMP}: ${requestHeaders[XHR.ORIGIN_TIMESTAMP]}]`
                : `[${CHAT_BLK} ${XHR.TRACEPARENT.toLowerCase()}: ${requestHeaders[XHR.TRACEPARENT]}]`;
            throwableMessage = `${prefix} Response Not Received: ${message}`;
            console.error(throwableMessage, {
                request,
                payload,
            });
        } else {
            console.error(`${prefix} ${name}: ${message}`, {
                payload,
            });
        }
        if (isThrowableError) {
            throw new Error(throwableMessage);
        }
    };

    /**
     * Sets the user's initials based on the current environment and global analytics parameters.
     *
     * - If `globalAnalyticsParams` is available, it will extract `userFirstName`, `userLastName`, and `emailAddress`.
     * - Update `chatService.username` to the full name if both `userFirstName` and `userLastName` are available, otherwise use `emailAddress`.
     * - Convert the `chatService.username` to initials and set it to `chatService.userInitials`.
     *
     * @returns {void}
     */
    public setUserInitials = (): void => {
        const {
            location: { hostname },
            globalAnalyticsParams,
        } = window as Window & {
            globalAnalyticsParams?: { [key: string]: string };
        };
        if (!URIS.PRE_PROD_HOSTS.includes(hostname) && globalAnalyticsParams) {
            const {
                userFirstName,
                userLastName,
                emailAddress = '',
            } = globalAnalyticsParams;

            this.username =
                userFirstName && userLastName
                    ? `${userFirstName} ${userLastName}`
                    : emailAddress;
            this.email = emailAddress;
        }

        this.userInitials =
            this.username
                ?.split(' ')
                .map((n) => n[0])
                .join('') || '';

        console.log(`[${CHAT_BLK}] user:`, {
            username: this.username,
            userInitials: this.userInitials,
            email: this.email,
        });
    };

    /**
     * Requests the user to log in and returns the username.
     *
     * If the application is running on localhost or a pre-production host, it attempts to fetch the logged-in user from the backend.
     * If the user is already logged in, it returns the stored username.
     * If the user is not logged in, it redirects to the production login path.
     *
     * @returns {Promise<string>} The username of the logged-in user.
     *
     * @throws Will log an error message if there is an issue fetching the user from the backend.
     */
    public requestUserLogin = async (): Promise<boolean> => {
        this.username = DEFAULT.USERNAME;
        try {
            if (
                URIS.PRE_PROD_HOSTS.includes(window.location.hostname) ||
                window['__CYPRESS_CHATBLK_ENV__'] === 'production'
            ) {
                const response = await ChatBlkBaseService.axiosInstance.get(
                    BLK_API_PATHS.BMS
                );
                this.email = response?.data?.login;
                console.log(`[${CHAT_BLK}] User email fetched: ${this.email}`);
            } else {
                if (!this.isLoggedIn) {
                    const { PROD_LOGIN_PATH, PRE_PROD_LOGIN_PATH } = URIS;
                    const queriedSigninPath = document
                        .querySelector('#signinBlock a')
                        ?.getAttribute('href');

                    //#endregion
                    window.location.replace(
                        queriedSigninPath ||
                            (window.location.hostname.startsWith('blk-one01')
                                ? PRE_PROD_LOGIN_PATH
                                : PROD_LOGIN_PATH)
                    );
                }
                this.username = this.username || DEFAULT.USERNAME;
            }
            this.isProduction = URIS.PROD_HOSTS.includes(
                window.location.hostname
            );
        } catch (error: unknown) {
            console.error(`[${CHAT_BLK}] login error:`, error);
        }
        this.setUserInitials();
        return this.isLoggedIn;
    };
}
